import React from 'react'
import useSWRImmutable from 'swr/immutable'

import { useI18N } from '../../../libraries/i18n/hook'
import SortingBar from '../../components/grid/SortingBar'
import Loading from '../../components/layout/Loading'
import { SORTING_OPTIONS } from '../../../libraries/content'
import CompanyGrid from '../../components/companies/Grid'
import search, { generateFilterClause, generateSortClause, SEARCH_HITS_INDEX } from '../../../libraries/search'
import { useCompanyFilter } from '../../../libraries/search/filters'
import NoResults from '../../components/layout/NoResults'
import type { CompanyFilter } from '../../../api/companies/types'

const addSortClause = (sortBy: string | undefined): string[] => {
  switch (sortBy) {
    case SORTING_OPTIONS.LATEST:
      return generateSortClause('updatedAt', 'desc')
    case SORTING_OPTIONS.ALPHABETIC:
      return generateSortClause('name', 'asc')
    case SORTING_OPTIONS.RECOMMENDED:
      return generateSortClause('sortPosition', 'asc')
    default:
      return [] // Default sorting will be handled by Meilisearch's ranking rules
  }
}

const DEFAULT_COMPANY_FILTERS: Partial<CompanyFilter> = {
  text: '',
  industryIds: [],
}

const CompaniesList: React.FC = () => {
  const { t } = useI18N()
  const { filters, setFilters } = useCompanyFilter()
  const { data: companiesData, isLoading, error } = useSWRImmutable(
    ['search', 'companies', filters],
    () => search(filters.text ?? '', SEARCH_HITS_INDEX.COMPANIES, generateFilterClause('industry.id', filters.industryIds), addSortClause(filters.sortBy))
  )
  const data = companiesData?.companies
  const { data: defaultCompaniesData } = useSWRImmutable(
    ['search', 'companies', 'default-filters'],
    () => search('', SEARCH_HITS_INDEX.COMPANIES, generateFilterClause('industry.id', undefined), addSortClause(filters.sortBy))
  )
  const totalResultsWithDefaultFilters = defaultCompaniesData?.companies?.length ?? 0
  const handleClearFilters = () => {
    setFilters(DEFAULT_COMPANY_FILTERS, true)
  }
  const sortingOptions = [
    { label: t('common.sortBy.recommended'), value: SORTING_OPTIONS.RECOMMENDED },
    { label: t('common.sortBy.alphabetically'), value: SORTING_OPTIONS.ALPHABETIC },
    { label: t('common.sortBy.latest'), value: SORTING_OPTIONS.LATEST }
  ]
  return (
    <div>
      {isLoading && <Loading size='fill-container' />}
      {!isLoading && (
        <div className='mt-4'>
          <SortingBar handleChange={setFilters} itemsAmount={data?.length || 0} options={sortingOptions} currentValue={filters.sortBy || SORTING_OPTIONS.RECOMMENDED} />
          {(data?.length ?? 0) === 0
            ? <NoResults onClearFilter={handleClearFilters} totalResults={totalResultsWithDefaultFilters} />
            : <CompanyGrid data={data} isLoading={isLoading} error={error} />
          }
        </div>
      )}
    </div>
  )
}

export default CompaniesList
